'use client'

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { 
  FaRegSadTear, 
  FaRegSmile, 
  FaRegFrown, 
  FaInfoCircle, 
  FaQuestionCircle,
  FaBook,
  FaLightbulb,
  FaLeaf
} from 'react-icons/fa';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const FoodSensitivityCalculator = () => {
  const [inputs, setInputs] = useState({
    foodItem: '',
    symptoms: '',
    severity: 'mild',
    duration: '',
    timestamp: new Date().toISOString()
  });
  const [results, setResults] = useState([]);
  const [showGraph, setShowGraph] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const trackSensitivity = () => {
    if (!inputs.foodItem || !inputs.symptoms) return;

    const newResult = {
      ...inputs,
      id: Date.now()
    };

    setResults(prev => [newResult, ...prev]);
    setShowGraph(true);
  };

  const pieData = {
    labels: ['Mild', 'Moderate', 'Severe'],
    datasets: [
      {
        data: [
          results.filter(result => result.severity === 'mild').length,
          results.filter(result => result.severity === 'moderate').length,
          results.filter(result => result.severity === 'severe').length
        ],
        backgroundColor: [
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 159, 64, 0.8)',
          'rgba(255, 99, 132, 0.8)'
        ],
        hoverBackgroundColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        hoverOffset: 8,
        borderWidth: 2,
        borderColor: 'white'
      }
    ]
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#495057',
          font: {
            size: 14
          }
        }
      },
      title: {
        display: true,
        text: 'Sensitivity Severity Distribution',
        color: '#2c3e50',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function(context) {
            let label = context.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed) {
              label += context.parsed + ' reactions';
            }
            return label;
          }
        }
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    }
  };

  return (
    <div>
      <Helmet>
        <title>Food Sensitivity Calculator - Track Your Food Reactions</title>
        <meta name="description" content="Track and identify food sensitivities and allergies. Monitor your reactions to different foods and understand your body better." />
        <meta property="og:title" content="Food Sensitivity Calculator" />
        <meta property="og:description" content="Track and identify food sensitivities and allergies. Monitor your reactions to different foods and understand your body better." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/images/food-sensitivity-calculator.jpg" />
      </Helmet>

      <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-green-600 mb-2">
            🍽️ Food Sensitivity Calculator
          </h1>
          <p className="text-gray-600">
            Track and identify food sensitivities
          </p>
        </div>

        <div className="space-y-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Food Item
            </label>
            <input
              type="text"
              name="foodItem"
              value={inputs.foodItem}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter food item name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Symptoms
            </label>
            <textarea
              name="symptoms"
              value={inputs.symptoms}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Describe any symptoms experienced (e.g., bloating, headache, skin rash)"
              rows="3"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Severity
            </label>
            <select
              name="severity"
              value={inputs.severity}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration (hours)
            </label>
            <input
              type="number"
              name="duration"
              value={inputs.duration}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter duration in hours"
            />
          </div>
        </div>

        <button
          onClick={trackSensitivity}
          className="w-full bg-gradient-to-r from-green-500 to-green-600 text-red py-2 px-4 rounded-md hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-green-500/50"
          disabled={!inputs.foodItem || !inputs.symptoms}
        >
          Track Sensitivity
        </button>

        {results.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Recent Sensitivity Records</h2>
            <div className="space-y-4">
              {results.map((result, index) => (
                <div key={result.id} className="p-4 bg-green-50 rounded-lg border border-green-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-lg">{result.foodItem}</h3>
                      <p className="text-sm text-gray-600">{new Date(result.timestamp).toLocaleString()}</p>
                    </div>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-medium text-center min-w-[80px] ${
                      result.severity === 'mild' ? 'bg-green-100 text-green-800' :
                      result.severity === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {result.severity.charAt(0).toUpperCase() + result.severity.slice(1)}
                    </span>
                  </div>
                  <div className="mt-2">
                    <p className="text-gray-600 font-medium mb-1">Symptoms:</p>
                    <p className="text-gray-700">{result.symptoms}</p>
                  </div>
                  <div className="mt-2 flex items-center">
                    <span className="text-gray-600 font-medium mr-1">Duration:</span>
                    <span className="text-gray-700 font-medium">{result.duration} hours</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Sensitivity Analysis</h2>
            <div className="h-64 bg-white rounded-lg shadow-lg p-4">
              <Pie data={pieData} options={pieOptions} />
              <div className="mt-4 text-center text-gray-600">
                <p>Total Reactions: {results.length}</p>
                <p className="mt-1">
                  <span className="text-green-600">{results.filter(r => r.severity === 'mild').length}</span> Mild •
                  <span className="text-yellow-600">{results.filter(r => r.severity === 'moderate').length}</span> Moderate •
                  <span className="text-red-600">{results.filter(r => r.severity === 'severe').length}</span> Severe
                </p>
              </div>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Analysis & Recommendations</h2>
            <div className="space-y-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Severity Analysis</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-green-400 mr-2"></span>
                    <p className="flex-1">Mild Reactions: {results.filter(r => r.severity === 'mild').length} ({Math.round((results.filter(r => r.severity === 'mild').length / results.length) * 100)}%)</p>
                  </div>
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-yellow-400 mr-2"></span>
                    <p className="flex-1">Moderate Reactions: {results.filter(r => r.severity === 'moderate').length} ({Math.round((results.filter(r => r.severity === 'moderate').length / results.length) * 100)}%)</p>
                  </div>
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-red-400 mr-2"></span>
                    <p className="flex-1">Severe Reactions: {results.filter(r => r.severity === 'severe').length} ({Math.round((results.filter(r => r.severity === 'severe').length / results.length) * 100)}%)</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Most Common Symptoms</h3>
                <div className="space-y-2">
                  {Object.entries(
                    results.reduce((acc, result) => {
                      const symptoms = result.symptoms.split(',').map(s => s.trim());
                      symptoms.forEach(symptom => {
                        acc[symptom] = (acc[symptom] || 0) + 1;
                      });
                      return acc;
                    }, {})
                  )
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 3)
                  .map(([symptom, count], index) => (
                    <div key={index} className="flex items-center">
                      <span className="w-4 h-4 rounded-full bg-green-400 mr-2"></span>
                      <p className="flex-1">{symptom}: {count} occurrences</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Duration Analysis</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-green-400 mr-2"></span>
                    <p className="flex-1">Average Duration: {Math.round(results.reduce((acc, r) => acc + parseInt(r.duration), 0) / results.length)} hours</p>
                  </div>
                  <div className="flex items-center">
                    <span className="w-4 h-4 rounded-full bg-yellow-400 mr-2"></span>
                    <p className="flex-1">Longest Duration: {Math.max(...results.map(r => parseInt(r.duration)))} hours</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Recommendations</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>
                    {results.filter(r => r.severity === 'severe').length > 0 && (
                      <span className="text-red-600">Consult a healthcare professional immediately if you experience severe reactions.</span>
                    )}
                  </li>
                  <li>
                    {results.filter(r => r.severity === 'moderate').length > 0 && (
                      <span className="text-yellow-600">Monitor moderate reactions closely and keep track of patterns.</span>
                    )}
                  </li>
                  <li>
                    {results.filter(r => r.severity === 'mild').length > 0 && (
                      <span className="text-green-600">Mild reactions may indicate food sensitivities - consider reducing consumption of these foods.</span>
                    )}
                  </li>
                  <li>
                    <span className="text-gray-600">Keep tracking your reactions to identify patterns and triggers.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="mt-16">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaBook className="mr-2" />
            How to Use
          </h2>
          <ol className="list-decimal list-inside text-gray-600 space-y-2">
            <li>Enter the food item you consumed</li>
            <li>Describe any symptoms experienced</li>
            <li>Select the severity level (Mild, Moderate, or Severe)</li>
            <li>Enter the duration of symptoms in hours</li>
            <li>Click "Track Sensitivity" to record your reaction</li>
            <li>View your sensitivity history and analysis</li>
          </ol>
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaLightbulb className="mr-2" />
            Features & Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Track Multiple Symptoms</h3>
              <p className="text-gray-600">
                Record different symptoms for each food item
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Severity Grading</h3>
              <p className="text-gray-600">
                Categorize reactions as mild, moderate, or severe
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Duration Tracking</h3>
              <p className="text-gray-600">
                Monitor how long symptoms last after consumption
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Visual Analysis</h3>
              <p className="text-gray-600">
                View severity distribution in a pie chart
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaQuestionCircle className="mr-2" />
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">What is a food sensitivity?</h3>
              <p className="text-gray-600">
                A food sensitivity is an adverse reaction to food that doesn't involve the immune system.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">How is this different from an allergy?</h3>
              <p className="text-gray-600">
                Food allergies involve the immune system and can be life-threatening, while sensitivities are generally less severe.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">How can tracking help?</h3>
              <p className="text-gray-600">
                Tracking helps identify patterns and triggers, making it easier to manage your diet.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">What should I do if I suspect an allergy?</h3>
              <p className="text-gray-600">
                Consult a healthcare professional if you suspect a food allergy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodSensitivityCalculator;
